import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import { fetchOrderReferenceData, fetchOrders, renewBulkOrders } from './api';
import DateField from './DateField';
import OrderAccess from './OrderAccess';
import OrderCodeField from './OrderCodeField';
import OrderSubjectField from './OrderSubjectField';
import PageHeader from './PageHeader';
import SearchableSelect from './SearchableSelect';
import { OrderDetails, OrderSummary, SelectOption } from './types';
import {
  followStartDate,
  getBulkRenewalPeriod,
  getDeadlineMax,
  getEarliestStartDate,
  getSharedOrderCode,
  isOrderRenewable,
  toOrderCode,
  validateBulkRenewal,
} from './utils';
import * as Styles from './Orders.css';

interface RenewalFields {
  startDate: string;
  endDate: string;
  decisionDeadline: string;
  subject: string;
  conditions: string;
  orderCode: string;
}

type RenewalError = 'validation' | 'forbidden' | 'request';

const DATE_LABELS = {
  startDate: 'ORDERS.START_DATE',
  endDate: 'ORDERS.END_DATE',
  decisionDeadline: 'ORDERS.DEADLINE',
};

const formatDate = (value?: string | null) =>
  value ? moment(value).format('DD/MM/YYYY') : '—';

const renewalErrorFromStatus = (status: number): RenewalError => {
  if (status === 403) {
    return 'forbidden';
  }
  if (status === 400 || status === 409) {
    return 'validation';
  }
  return 'request';
};

const renewalErrorMessage = (error: RenewalError) => {
  if (error === 'forbidden') {
    return 'ORDERS.ACTION_ERROR.UNAUTHORIZED';
  }
  if (error === 'validation') {
    return 'ORDERS.BULK.RENEW_VALIDATION_ERROR';
  }
  return 'ORDERS.BULK.ERROR';
};

const EMPTY_FIELDS: RenewalFields = {
  startDate: '',
  endDate: '',
  decisionDeadline: '',
  subject: '',
  conditions: '',
  orderCode: '',
};

// Shared successor dates for the selection, in selection order.
const prefillDates = (
  current: RenewalFields,
  selection: OrderSummary[],
): RenewalFields => {
  const period = getBulkRenewalPeriod(selection);
  if (!period) {
    return current;
  }
  return {
    ...current,
    startDate: period.startDate.format('YYYY-MM-DD'),
    endDate: period.endDate.format('YYYY-MM-DD'),
    decisionDeadline: period.decisionDeadline.format('YYYY-MM-DD'),
  };
};

export const BulkOrderRenewContent = () => {
  const intl = useIntl();
  // Change request of 30.10: starts may fall in the month not yet closed.
  const earliestStart = getEarliestStartDate().format('YYYY-MM-DD');
  const [clients, setClients] = useState<SelectOption[]>([]);
  const [clientId, setClientId] = useState('');
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [fields, setFields] = useState<RenewalFields>(EMPTY_FIELDS);
  // Dates follow the selection until the manager edits one of them.
  const [datesTouched, setDatesTouched] = useState(false);
  const deadlineTouched = useRef(false);
  const codeTouched = useRef(false);
  const [previewed, setPreviewed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referenceError, setReferenceError] = useState(false);
  const [error, setError] = useState<RenewalError | null>(null);
  const [errorDetail, setErrorDetail] = useState('');
  const [validationMessage, setValidationMessage] = useState('');
  const [successors, setSuccessors] = useState<OrderDetails[]>([]);
  const requestRevision = useRef(0);

  useEffect(() => {
    let mounted = true;
    fetchOrderReferenceData().then((response) => {
      if (!mounted) {
        return;
      }
      setClients(response.activeClients);
      setReferenceError(!response.clientsOk);
    });
    return () => {
      mounted = false;
      requestRevision.current += 1;
    };
  }, []);

  const invalidate = () => {
    requestRevision.current += 1;
    setLoading(false);
    setPreviewed(false);
    setValidationMessage('');
    setSuccessors([]);
    setError(null);
    setErrorDetail('');
  };

  const loadClientOrders = async (value: string) => {
    invalidate();
    setClientId(value);
    setSelected([]);
    setOrders([]);
    setDatesTouched(false);
    deadlineTouched.current = false;
    codeTouched.current = false;
    setFields((current) => ({
      ...current,
      startDate: '',
      endDate: '',
      decisionDeadline: '',
    }));
    if (!value) {
      return;
    }
    const revision = requestRevision.current + 1;
    requestRevision.current = revision;
    setLoading(true);
    const filters = { clientId: Number(value), renewable: true };
    const response = await fetchOrders(filters, 0, 200);
    if (revision !== requestRevision.current) {
      return;
    }
    if (response.ok && response.data) {
      const pages = [response.data.content];
      for (let page = 1; page < response.data.totalPages; page += 1) {
        const next = await fetchOrders(filters, page, 200);
        if (revision !== requestRevision.current) {
          return;
        }
        if (!next.ok || !next.data) {
          setLoading(false);
          setError(next.status === 403 ? 'forbidden' : 'request');
          return;
        }
        pages.push(next.data.content);
      }
      setLoading(false);
      // Older backends ignore the renewable filter; apply the rule here too.
      setOrders(pages.flat().filter(isOrderRenewable));
    } else {
      setLoading(false);
      setError(response.status === 403 ? 'forbidden' : 'request');
    }
  };

  const toOrders = (numbers: string[]) =>
    numbers
      .map((number) => orders.find((order) => order.orderNumber === number))
      .filter((order): order is OrderSummary => Boolean(order));

  const changeSelection = (next: string[]) => {
    invalidate();
    setSelected(next);
    if (!datesTouched) {
      setFields((current) => prefillDates(current, toOrders(next)));
    }
    // An extension keeps its predecessor's prefix: the one the selection
    // shares, until the manager edits it (D7).
    if (!codeTouched.current) {
      setFields((current) => ({
        ...current,
        orderCode: getSharedOrderCode(toOrders(next)),
      }));
    }
  };

  const chosenOrders = toOrders(selected);
  const allSelected = orders.length > 0 && selected.length === orders.length;

  const preview = () => {
    const message = validateBulkRenewal(
      {
        clientId: Number(clientId),
        orderNumbers: selected,
        ...fields,
      },
      chosenOrders,
    );
    setValidationMessage(message);
    setPreviewed(!message);
  };

  const submit = async () => {
    if (!previewed) {
      return;
    }
    const revision = requestRevision.current + 1;
    requestRevision.current = revision;
    setLoading(true);
    setError(null);
    setErrorDetail('');
    const response = await renewBulkOrders({
      clientId: Number(clientId),
      orderNumbers: selected,
      ...fields,
      subject: fields.subject.trim() || undefined,
      conditions: fields.conditions.trim() || undefined,
      orderCode: toOrderCode(fields.orderCode),
    });
    if (revision !== requestRevision.current) {
      return;
    }
    setLoading(false);
    if (response.ok && response.data) {
      setSuccessors(response.data);
    } else {
      setError(renewalErrorFromStatus(response.status));
      setErrorDetail(
        [400, 409].includes(response.status) ? response.message || '' : '',
      );
    }
  };

  return (
    <Styles.Page>
      <PageHeader
        title={intl.formatMessage({ id: 'ORDERS.BULK.RENEW_TITLE' })}
        description={intl.formatMessage({
          id: 'ORDERS.BULK.RENEW_DESCRIPTION',
        })}
        actions={
          <Button component={Link} to="/assignments">
            {intl.formatMessage({ id: 'ORDERS.BACK' })}
          </Button>
        }
      />

      {referenceError && (
        <Alert severity="error">
          {intl.formatMessage({ id: 'ORDERS.REFERENCE_ERROR' })}
        </Alert>
      )}
      {error && (
        <Alert severity={error === 'validation' ? 'warning' : 'error'}>
          {intl.formatMessage({ id: renewalErrorMessage(error) })}
          {errorDetail && <div>{errorDetail}</div>}
        </Alert>
      )}
      {validationMessage && (
        <Alert severity="warning">
          {intl.formatMessage({ id: validationMessage })}
        </Alert>
      )}

      <Styles.FormPaper>
        <SearchableSelect
          id="bulk-renew-client"
          required
          fullWidth
          size="medium"
          label={intl.formatMessage({ id: 'ORDERS.CLIENT' })}
          options={clients.map(({ value, name }) => ({ value, label: name }))}
          value={clientId}
          onChange={loadClientOrders}
        />

        {loading && (
          <Box p={3} textAlign="center">
            <CircularProgress />
          </Box>
        )}
        {!loading && clientId && orders.length === 0 && (
          <Box mt={2}>
            <Alert severity="info">
              {intl.formatMessage({ id: 'ORDERS.BULK.RENEW_EMPTY' })}
            </Alert>
          </Box>
        )}
        {orders.length > 0 && (
          <Box mt={2}>
            <Table
              size="small"
              aria-label={intl.formatMessage({
                id: 'ORDERS.BULK.RENEW_TITLE',
              })}
            >
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      inputProps={{
                        'aria-label': intl.formatMessage({
                          id: 'ORDERS.BULK.SELECT_ALL',
                        }),
                      }}
                      checked={allSelected}
                      indeterminate={selected.length > 0 && !allSelected}
                      onChange={() =>
                        changeSelection(
                          allSelected
                            ? []
                            : orders.map(({ orderNumber }) => orderNumber),
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {intl.formatMessage({ id: 'ORDERS.NUMBER' })}
                  </TableCell>
                  <TableCell>
                    {intl.formatMessage({ id: 'ORDERS.CONSULTANT' })}
                  </TableCell>
                  <TableCell>
                    {intl.formatMessage({ id: 'ORDERS.PERIOD' })}
                  </TableCell>
                  <TableCell>
                    {intl.formatMessage({ id: 'ORDERS.STATUS' })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => {
                  const checked = selected.includes(order.orderNumber);
                  return (
                    <Styles.ClickableRow
                      key={order.orderNumber}
                      hover
                      selected={checked}
                      onClick={() =>
                        changeSelection(
                          checked
                            ? selected.filter(
                                (number) => number !== order.orderNumber,
                              )
                            : [...selected, order.orderNumber],
                        )
                      }
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          inputProps={{ 'aria-label': order.orderNumber }}
                          checked={checked}
                        />
                      </TableCell>
                      <Styles.NoWrapCell>{order.orderNumber}</Styles.NoWrapCell>
                      <TableCell>
                        {order.consultantName}
                        {order.contractType && (
                          <Styles.CellNote>
                            {intl.formatMessage({
                              id: `ORDERS.CONTRACT.${order.contractType}`,
                              defaultMessage: order.contractType,
                            })}
                          </Styles.CellNote>
                        )}
                      </TableCell>
                      <Styles.NoWrapCell>
                        {formatDate(order.startDate)} –{' '}
                        {formatDate(order.effectiveEndDate || order.endDate)}
                      </Styles.NoWrapCell>
                      <TableCell>
                        {intl.formatMessage({
                          id: `ORDERS.STATUS.${order.status}`,
                        })}
                      </TableCell>
                    </Styles.ClickableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        <Styles.FormGrid>
          {(['startDate', 'endDate', 'decisionDeadline'] as const).map(
            (key) => (
              <DateField
                key={key}
                id={`bulk-renew-${key}`}
                required
                disablePast={key === 'decisionDeadline'}
                min={key === 'decisionDeadline' ? undefined : earliestStart}
                label={intl.formatMessage({ id: DATE_LABELS[key] })}
                max={
                  key === 'decisionDeadline'
                    ? getDeadlineMax(fields.startDate)
                    : undefined
                }
                value={fields[key]}
                onChange={(value) => {
                  invalidate();
                  setDatesTouched(true);
                  // An untouched deadline follows the start.
                  if (key === 'decisionDeadline') {
                    deadlineTouched.current = true;
                  }
                  setFields((current) => ({
                    ...current,
                    [key]: value,
                    ...(key === 'startDate' && !deadlineTouched.current
                      ? {
                          decisionDeadline: followStartDate(
                            value,
                            current.decisionDeadline,
                          ),
                        }
                      : {}),
                  }));
                }}
              />
            ),
          )}
          <OrderCodeField
            id="bulk-renew-code"
            value={fields.orderCode}
            onChange={(value) => {
              invalidate();
              codeTouched.current = true;
              setFields((current) => ({ ...current, orderCode: value }));
            }}
          />
          <Styles.FullWidth>
            <Typography variant="caption" color="textSecondary">
              {intl.formatMessage({ id: 'ORDERS.BULK.RENEW_DATES_HELP' })}
            </Typography>
          </Styles.FullWidth>
          <Styles.FullWidth>
            <OrderSubjectField
              id="bulk-renew-subject"
              help="ORDERS.BULK.RENEW_SUBJECT_HELP"
              value={fields.subject}
              onChange={(value) => {
                invalidate();
                setFields((current) => ({ ...current, subject: value }));
              }}
            />
          </Styles.FullWidth>
          <Styles.FullWidth>
            <TextField
              id="bulk-renew-conditions"
              fullWidth
              multiline
              minRows={5}
              variant="outlined"
              inputProps={{ maxLength: 10000 }}
              label={intl.formatMessage({ id: 'ORDERS.CONDITIONS' })}
              helperText={intl.formatMessage({
                id: 'ORDERS.BULK.RENEW_CONDITIONS_HELP',
              })}
              value={fields.conditions}
              onChange={(event) => {
                const { value } = event.target;
                invalidate();
                setFields((current) => ({ ...current, conditions: value }));
              }}
            />
          </Styles.FullWidth>
        </Styles.FormGrid>

        {!previewed && !successors.length && (
          <Box display="flex" justifyContent="flex-end">
            <Button
              color="primary"
              variant="contained"
              onClick={preview}
              disabled={loading || !selected.length}
            >
              {intl.formatMessage({ id: 'ORDERS.BULK.PREVIEW' })}
            </Button>
          </Box>
        )}

        {previewed && !successors.length && (
          <Box mt={2}>
            <Alert severity="success">
              {intl.formatMessage(
                { id: 'ORDERS.BULK.RENEW_PREVIEW' },
                {
                  count: chosenOrders.length,
                  date: formatDate(fields.startDate),
                  end: formatDate(fields.endDate),
                  deadline: formatDate(fields.decisionDeadline),
                },
              )}
            </Alert>
            <Box
              mt={2}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gridGap={16}
              flexWrap="wrap"
            >
              <Typography variant="body2" color="textSecondary">
                {intl.formatMessage({ id: 'ORDERS.BULK.ATOMIC_WARNING' })}
              </Typography>
              <Box display="flex" gridGap={8}>
                <Button onClick={() => setPreviewed(false)} disabled={loading}>
                  {intl.formatMessage({ id: 'ORDERS.BACK_ACTION' })}
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={loading}
                  onClick={submit}
                >
                  {loading ? (
                    <CircularProgress size={22} />
                  ) : (
                    intl.formatMessage(
                      { id: 'ORDERS.BULK.RENEW_SUBMIT_COUNT' },
                      { count: chosenOrders.length },
                    )
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        {successors.length > 0 && (
          <Box mt={2}>
            <Alert severity="success">
              {intl.formatMessage(
                { id: 'ORDERS.BULK.RENEW_SUCCESS' },
                { count: successors.length },
              )}
            </Alert>
            <ul>
              {successors.map(({ order }) => (
                <li key={order.orderNumber}>
                  <Link
                    to={`/assignments/${encodeURIComponent(order.orderNumber)}`}
                  >
                    {order.orderNumber}
                  </Link>{' '}
                  — {order.consultantName}
                </li>
              ))}
            </ul>
          </Box>
        )}
      </Styles.FormPaper>
    </Styles.Page>
  );
};

const BulkOrderRenew = () => (
  <OrderAccess requireManage>{() => <BulkOrderRenewContent />}</OrderAccess>
);

export default BulkOrderRenew;
